import * as React from 'react';

import ActivityRow from './activity_row';
import MultipleDiagnosticsModal from './multipleDiagnosticsModal';
import { Activity, Topic, } from './interfaces';

import { Snackbar, SortableList, DragHandle, defaultSnackbarTimeout, } from '../../../../../Shared/index';
import useSnackbarMonitor from '../../../../../Shared/hooks/useSnackbarMonitor';
import { DIAGNOSTIC, } from '../../../../../Shared/utils/constants';

const reorderSrc = `${window.postbuildEnv.CDN_URL}/images/icons/reorder.svg`

interface AssignButtonProps {
  selectedActivities: Activity[],
  handleClickContinue: (event: any) => void,
  saveButtonEnabled: boolean,
  isStaff: boolean
}

interface HeaderProps {
  handleClickContinue: (event: any) => void,
  selectedActivities: Activity[],
  setSelectedActivities: (selectedActivities: Activity[]) => void,
  toggleActivitySelection: (activity: Activity, isSelected: boolean) => void,
  saveButtonEnabled: boolean,
  isStaff: boolean,
  topics: Topic[]
}

const AssignButton = ({ selectedActivities, handleClickContinue, saveButtonEnabled, isStaff, }: AssignButtonProps) => {
  let action = handleClickContinue
  let buttonClass = 'quill-button contained large green focus-on-light';
  const buttonCopy = isStaff ? 'Save' : 'Assign'
  if (isStaff) {
    if (!saveButtonEnabled) {
      buttonClass += ' disabled';
      action = null
    }
  } else if (!(selectedActivities && selectedActivities.length)) {
    buttonClass += ' disabled';
    action = null
  }
  return <button className={buttonClass} onClick={action} type="button">{buttonCopy}</button>
}

const Header = ({ handleClickContinue, selectedActivities, setSelectedActivities, toggleActivitySelection, saveButtonEnabled, isStaff, topics, }: HeaderProps) => {
  const [showActivities, setShowActivities] = React.useState(false)
  const [showSnackbar, setShowSnackbar] = React.useState(false)
  const [showMultipleDiagnosticsModal, setShowMultipleDiagnosticsModal] = React.useState(false)

  useSnackbarMonitor(showSnackbar, setShowSnackbar, defaultSnackbarTimeout)

  let action = handleClickContinue

  React.useEffect(() => {
    if (!selectedActivities.length && showActivities) {
      setShowActivities(false)
    }
  }, [selectedActivities])

  function toggleShowActivities() { setShowActivities(!showActivities)}

  function closeMultipleDiagnosticsModal() { setShowMultipleDiagnosticsModal(false) }

  function handleClickAssign(event: any) {
    const selectedDiagnosticCount = selectedActivities.filter(activity => activity.activity_classification?.key === DIAGNOSTIC).length
    if (selectedDiagnosticCount > 1) {
      setShowMultipleDiagnosticsModal(true)
      return
    }
    handleClickContinue(event)
  }

  function sortCallback(sortInfo) {
    const newOrder = sortInfo.map(item => item.key);
    const newOrderedActivities = newOrder.map((key, i) => selectedActivities.find(a => a.id === Number(key)))
    setSelectedActivities(newOrderedActivities)
  }

  let headerContent = <h1>Choose activities</h1>
  let className = ''
  if (selectedActivities.length) {
    className = 'has-selected-activities'
    const text = selectedActivities.length > 1 ? `${selectedActivities.length} activities selected` : '1 activity selected'
    const buttonCopy = showActivities ? 'Hide' : 'View'
    const numberOfActivitiesForMobile = showActivities ? '' : <span className="number-of-activities">({selectedActivities.length})</span>
    headerContent = (<div>
      <h1>{text}</h1>
      <button aria-controls="selectedActivities" aria-expanded={showActivities} aria-label={`${buttonCopy} Selected Activities`} className="focus-on-light quill-button large grey outlined focus-on-light" onClick={toggleShowActivities} type="button">{buttonCopy}{numberOfActivitiesForMobile}</button>
    </div>)
  }

  let selectedActivitySection

  if (showActivities) {
    const selectedActivityRows = selectedActivities.map((a, i) => {
      const className = `selected-activity-row ${i === selectedActivities.length - 1 && 'is-last'}`
      // using a div as the outer element instead of a button here because something about default button behavior overrides the keypress handling by sortablehandle
      return (
        <section className={className} id={a.id} key={a.id}>
          <DragHandle><div className="focus-on-light" role="button" tabIndex={0}><img alt="Reorder icon" className="reorder-icon" src={reorderSrc} /></div></DragHandle>
          <ActivityRow
            activity={a}
            allTopics={topics}
            isSelected={true}
            setShowSnackbar={setShowSnackbar}
            showCheckbox={false}
            showRemoveButton={true}
            toggleActivitySelection={toggleActivitySelection}
          />
        </section>
      )
    })
    selectedActivitySection = <SortableList data={selectedActivityRows} helperClass="dragged-sortable-selected-activity-row" id="selectedActivities" sortCallback={sortCallback} useDragHandle={true} />
  }

  return (
    <header className={className}>
      {showMultipleDiagnosticsModal && <MultipleDiagnosticsModal handleCloseModal={closeMultipleDiagnosticsModal} />}
      <Snackbar text="Activity removed" visible={showSnackbar} />
      <div className="header-content">
        {headerContent}
        <AssignButton handleClickContinue={isStaff ? handleClickContinue : handleClickAssign} isStaff={isStaff} saveButtonEnabled={saveButtonEnabled} selectedActivities={selectedActivities} />
      </div>
      {selectedActivitySection}
    </header>
  )
}

export default Header
